import {
    ProFormDigit,
    ProFormGroup,
    ProFormItem,
    ProFormList,
    ProFormRadio,
    ProFormSelect,
    ProFormSwitch,
    ProFormText
} from '@ant-design/pro-form';
import React, { forwardRef, useContext, useEffect, useImperativeHandle } from 'react';
import ProCard from '@ant-design/pro-card';
import { Badge, Button, Form, Modal, Result, Tabs, Tooltip } from 'antd';
import { DeleteOutlined, DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import { useAtom, useAtomValue } from 'jotai';
import axiosApiInstance from '../../../api/axiosClient';
import { DDEXPageContext, isModalOpenAtom, PageContextProvider } from './DDEXMainPage';
import StepsFormFirstPage from './StepFormFirstPage';
import StepsFormSecondPage from './StepsFormSecondPage';
import { Store } from 'antd/es/form/interface';
import { useNavigate, useParams, redirect } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { strict } from 'assert';
import StepsFormThirdPage from './StepsFormThirdPage';
import { countryCodes } from '../../../common/ddex_options';
import {
    artTracksFormAtom,
    combinedFormDataAtom,
    currentStepAtom,
    releaseInfoFormAtom,
    submitFormAtom,
    uploadProgressAtom,
    useArtTracksForm,
    useReleaseInfoForm,
    useSubmitForm
} from './release_form_atoms';

export const checksEnabled = false;
// export const checksEnabled = true;
// const REDIRECT_URL = '/music';
const REDIRECT_URL = -1;

const getExcludedTerritories = (q: string) =>
    countryCodes.filter((element) => !q.split(/[,;]+/).includes(element)).join(',');

export {fetchAssetLabelOptions, fetchLabelOptions} from './labelOptions';

export const fetchArtistOptions = async () =>
    axiosApiInstance
        .get<API.MusicDistribution.Artist[]>(`/api/products/artists/`)
        .then(({ data }) => data)
        .then((artists) =>
            artists.map((artist) => ({
                label: artist.key_name,
                value: artist.key_name
            }))
        )
        .catch((reason) => {
            console.error(reason);
            return [];
        });

const ReleaseUpdateStepForm = () => {
    const { releasesActionRef } = useContext(DDEXPageContext);
    const navigate = useNavigate();
    const params = useParams();
    const [currentTab, setCurrentTab] = useAtom(currentStepAtom);
    const [uploadProgress, setUploadProgress] = useAtom(uploadProgressAtom);
    const [releaseInfoForm] = Form.useForm();
    const [artTracksForm] = Form.useForm();
    const [submitForm] = Form.useForm();
    const [, setReleaseInfoFormAtom] = useAtom(useReleaseInfoForm);
    const [, setArtTracksFormAtom] = useAtom(useArtTracksForm);
    const [, setSubmitFormAtom] = useAtom(useSubmitForm);
    const [releaseInfoData, setReleaseInfoData] = useAtom(releaseInfoFormAtom);
    const [artTracksData, setArtTracksData] = useAtom(artTracksFormAtom);
    const [submitData, setSubmitData] = useAtom(submitFormAtom);
    const combinedData = useAtomValue(combinedFormDataAtom);

    const { data, status } = useQuery({
        queryKey: ['release', params.upc_ean],
        queryFn: ({ queryKey }) => {
            console.log('query key: ', queryKey);
            return axiosApiInstance
                .get<API.MusicDistribution.MusicRelease>(`/api/products/releases/${queryKey[1]}`, {
                    params: {
                        with_label: '',
                        with_artists: '',
                        with_track_artists: ''
                    }
                })
                .then(({ data }) => {
                    // console.log(data);
                    const cover_art_file = data.album_cover_filename
                        ? [{ name: data.album_cover_filename }]
                        : [];
                    // console.log({ cover_art_file });
                    return {
                        ...data,
                        p_line: data.p_line ?? data.label?.p_line ?? '',
                        c_line: data.c_line ?? data.label?.c_line ?? '',
                        cover_art_file,
                        artists: data.artists.map(({ key_name }) => key_name),
                        featuring_artists: data.featuring_artists.map(({ key_name }) => key_name),
                        territories: (data.territories as string).split(';'),
                        art_tracks: data.art_tracks.map((track) => ({
                            ...track,
                            audio_file: track.filename ? [{ name: track.filename }] : [],
                            main_artists: track.main_artists.map(({ key_name }) => key_name),
                            featuring_artists: track.featuring_artists.map(({ key_name }) => key_name),
                            composers: track.composers.map(({ key_name }) => key_name),
                            authors: track.authors.map(({ key_name }) => key_name),
                            remixers: track.remixers.map(({ key_name }) => key_name),
                            producers: track.producers.map(({ key_name }) => key_name),
                            arrangers: track.arrangers.map(({ key_name }) => key_name),
                            publishers: track.publishers.map(({ key_name }) => key_name),
                            recording_engineers: (track.recording_engineers ?? []).map(({ key_name }) => key_name),
                            mixing_engineers: (track.mixing_engineers ?? []).map(({ key_name }) => key_name),
                            mastering_engineers: (track.mastering_engineers ?? []).map(({ key_name }) => key_name),
                            executive_producers: (track.executive_producers ?? []).map(({ key_name }) => key_name),
                        }))
                    };
                })
                .catch((reason) => {
                    console.error(reason);
                    return [];
                });
        }
    });

    useEffect(() => {
        setReleaseInfoFormAtom(releaseInfoForm);
        setArtTracksFormAtom(artTracksForm);
        setSubmitFormAtom(submitForm);
    }, [releaseInfoForm, artTracksForm, submitForm, setReleaseInfoFormAtom, setArtTracksFormAtom, setSubmitFormAtom]);

    useEffect(() => {
        if (data && status === 'success') {
            releaseInfoForm.setFieldsValue(data);
            setReleaseInfoData(data);

            artTracksForm.setFieldsValue(data);
            setArtTracksData(data);

            submitForm.setFieldsValue(data);
            setSubmitData(data);
        }
    }, [data, status, releaseInfoForm, artTracksForm, submitForm]);

    const handleFirstPageFlip = async (
        main_artists: API.MusicDistribution.Artist[],
        featuring_artists: API.MusicDistribution.Artist[]
    ) => {
        console.log('handleFirstPageFlip: ', main_artists, featuring_artists);
        const tracks = artTracksForm.getFieldValue('art_tracks');
        console.log('tracks: ', tracks);
        artTracksForm.setFieldValue(
            'art_tracks',
            tracks.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))
        );
        // Update the atom with the new data
        setArtTracksData(artTracksForm.getFieldsValue());
        console.log('end handleFirstPageFlip');
    };

    const handleTabChange = (activeKey: string) => {
        if (currentTab === 'release-info' && activeKey !== 'release-info' && releaseInfoForm) {
            setReleaseInfoData((prev) => ({...prev, ...releaseInfoForm.getFieldsValue()}));
        }
        setCurrentTab(activeKey);
    };

    const handleReleaseInfoFinish = async (values: any) => {
        setReleaseInfoData(values);
        await handleFirstPageFlip(values.artists, values.featuring_artists);
        setCurrentTab('art-tracks');
    };

    const handleArtTracksFinish = async (values: any) => {
        setArtTracksData(values);
        setCurrentTab('submit');
    };

    const renderMap = {
        error: <Result status='error' />,
        loading: <ProCard loading />
    };

    const handleSubmit = async () => {
        // Validate all forms
        try {
            await releaseInfoForm.validateFields();
            await artTracksForm.validateFields();

            // Combine all form data
            const values = {
                ...releaseInfoForm.getFieldsValue(),
                ...artTracksForm.getFieldsValue(),
                ...submitForm.getFieldsValue()
            };

            // Format territories
            const territories =
                values.territories instanceof Array
                    ? values.territories.join(';')
                    : values.territories === ''
                    ? 'Worldwide'
                    : values.territories;

            const excluded_territories = getExcludedTerritories(territories);

            await axiosApiInstance.delete(`/api/products/releases/${values.upc_ean}/main_artists`);

            const formattedValues = {
                ...values,
                territories,
                excluded_territories,
                artists: values.artists.map((key_name) => ({ key_name })),
                featuring_artists: values.featuring_artists?.map((key_name) => ({ id: key_name })),
                art_tracks: values.art_tracks.map((track) => ({
                    ...track,
                    main_artists: track.main_artists?.map((key_name) => ({ key_name })),
                    featuring_artists: track.featuring_artists?.map((key_name) => ({ key_name })),
                    composers: track.composers?.map((key_name) => ({ key_name })),
                    authors: track.authors?.map((key_name) => ({ key_name })),
                    remixers: track.remixers?.map((key_name) => ({ key_name })),
                    producers: track.producers?.map((key_name) => ({ key_name })),
                    arrangers: track.arrangers?.map((key_name) => ({ key_name })),
                    publishers: track.publishers?.map((key_name) => ({ key_name })),
                    recording_engineers: track.recording_engineers?.map((key_name) => ({ key_name })),
                    mixing_engineers: track.mixing_engineers?.map((key_name) => ({ key_name })),
                    mastering_engineers: track.mastering_engineers?.map((key_name) => ({ key_name })),
                    executive_producers: track.executive_producers?.map((key_name) => ({ key_name })),
                }))
            };

            console.log({ formattedValues });
            await axiosApiInstance
                .put(`/api/products/releases/`, formattedValues, {
                    params: {
                        strict: true
                    }
                })
                .catch((reason) => console.log(reason))
                .finally(() => navigate(REDIRECT_URL));
            return true;
        } catch (error) {
            console.error('Validation failed:', error);
            return false;
        }
    };

    if (status !== 'success') {
        return (
            <Modal
                width={1600}
                onCancel={() => navigate(REDIRECT_URL)}
                footer={null}
                destroyOnClose
                open
            >
                <div style={{ marginTop: 36, marginBottom: 20 }}>
                    {renderMap[status]}
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            width={1600}
            onCancel={() => navigate(REDIRECT_URL)}
            footer={[
                <Button key="back" onClick={() => navigate(REDIRECT_URL)}>
                    Cancel
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    onClick={handleSubmit}
                    disabled={currentTab !== 'submit'}
                >
                    Submit
                </Button>,
                currentTab === 'release-info' && (
                    <Button 
                        key="next-release-info" 
                        type="primary" 
                        onClick={() => releaseInfoForm.submit()}
                    >
                        Next
                    </Button>
                ),
                currentTab === 'art-tracks' && (
                    <Button 
                        key="next-art-tracks" 
                        type="primary" 
                        onClick={() => artTracksForm.submit()}
                    >
                        Next
                    </Button>
                )
            ]}
            destroyOnClose
            open
        >
            <div style={{ marginTop: 36, marginBottom: 20 }}>
                <Tabs 
                    activeKey={currentTab} 
                    onChange={handleTabChange}
                    type="card"
                    items={[
                        {
                            key: 'release-info',
                            label: 'Release Information',
                            children: (
                                <Form
                                    form={releaseInfoForm}
                                    layout='horizontal'
                                    labelAlign='left'
                                    preserve
                                    onFinish={handleReleaseInfoFinish}
                                >
                                    <StepsFormFirstPage edit={true} />
                                </Form>
                            )
                        },
                        {
                            key: 'art-tracks',
                            label: 'Tracks',
                            children: (
                                <Form
                                    form={artTracksForm}
                                    onFinish={handleArtTracksFinish}
                                >
                                    <StepsFormSecondPage edit={true} />
                                </Form>
                            )
                        },
                        {
                            key: 'submit',
                            label: 'Submit',
                            children: (
                                <Form
                                    form={submitForm}
                                >
                                    <StepsFormThirdPage />
                                </Form>
                            )
                        }
                    ]}
                />
            </div>
        </Modal>
    );
};

export default ReleaseUpdateStepForm;
